import {
    Badge,
    Button,
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
    CommandSeparator,
    Icon,
    Popover,
    PopoverContent,
    PopoverTrigger,
    Separator,
} from "@/shared/components";
import { cn } from "@/shared/utils";
import { Column, RowData } from "@tanstack/react-table";
import * as React from "react";
import type { DataTableFeatures } from "./data-table-features";

export interface MultiSelectFilter {
    column: string;
    title: string;
    options: {
        label: string;
        value: string;
    }[];
    defaultValue?: string[];
}

type DataTableMultiSelectFilterProps<TData extends RowData, TValue> = {
    column?: Column<DataTableFeatures, TData, TValue>;
    title?: string;
    options: {
        label: string;
        value: string;
        icon?: React.ComponentType<{ className?: string }>;
    }[];
};

export function DataTableMultiSelectFilter<TData extends RowData, TValue>({
    column,
    title,
    options,
}: DataTableMultiSelectFilterProps<TData, TValue>) {
    const selectedValues = new Set(column?.getFilterValue() as string[]);

    return (
        <Popover>
            <PopoverTrigger asChild>
                <Button variant="secondary" size="s" className="border-default border">
                    <Icon name="filter-alt" size="xs" />
                    {title}
                    {selectedValues?.size > 0 && (
                        <>
                            <Separator orientation="vertical" className="mx-2 h-4" />
                            <Badge variant="blue-subtle" className="rounded-sm px-1 font-normal lg:hidden">
                                {selectedValues.size}
                            </Badge>
                            <div className="hidden gap-1 lg:flex">
                                {selectedValues.size > 2 ? (
                                    <Badge variant="gray-subtle" className="rounded-sm px-1 font-normal">
                                        {selectedValues.size} geselecteerd
                                    </Badge>
                                ) : (
                                    options
                                        .filter((option) => selectedValues.has(option.value))
                                        .map((option) => (
                                            <Badge variant="orange-subtle" key={option.value} className="rounded-sm px-1 font-normal">
                                                {option.label}
                                            </Badge>
                                        ))
                                )}
                            </div>
                        </>
                    )}
                </Button>
            </PopoverTrigger>
            <PopoverContent className="max-w-[300px] p-0" align="start">
                <Command>
                    <CommandInput placeholder={title} />
                    <CommandList>
                        <CommandEmpty>No results found.</CommandEmpty>
                        <CommandGroup>
                            {options.map((option) => {
                                const isSelected = selectedValues.has(option.value);
                                return (
                                    <CommandItem
                                        key={option.value}
                                        className="data-[selected=true]:bg-hover"
                                        onSelect={() => {
                                            if (isSelected) {
                                                selectedValues.delete(option.value);
                                            } else {
                                                selectedValues.add(option.value);
                                            }
                                            const filterValues = Array.from(selectedValues);
                                            column?.setFilterValue(filterValues.length ? filterValues : undefined);
                                        }}
                                    >
                                        <div
                                            className={cn(
                                                "flex size-4 items-center justify-center rounded-[4px] border",
                                                isSelected
                                                    ? "bg-primary border-primary text-primary-foreground"
                                                    : "border-input [&_svg]:invisible"
                                            )}
                                        >
                                            <Icon name="check" size="xs" className="text-primary-foreground" />
                                        </div>
                                        {option.icon && <option.icon className="text-muted-foreground size-4" />}
                                        <span>{option.label}</span>
                                    </CommandItem>
                                );
                            })}
                        </CommandGroup>
                        {selectedValues.size > 0 && (
                            <>
                                <CommandSeparator />
                                <CommandGroup>
                                    <CommandItem onSelect={() => column?.setFilterValue(undefined)} className="justify-center text-center">
                                        Filters wissen
                                    </CommandItem>
                                </CommandGroup>
                            </>
                        )}
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}
